import React, { useState } from "react";

//App
import { OrganizationDto } from "../../app/model/api/types/dto/organizationDto";
import { OrganizationProps } from "../../types/organization";
import { OrganizationFormContainer } from "./OrganizationFormContainer";
import { useOrganization } from "./hooks/useOrganization";

//Common
import {
  DataGrid,
  ConfirmationDialog,
  Snackbar,
  Drawer,
  Chip,
} from "../common/VFX-UI";
import { MoreActionButton } from "../common/Buttons";
import PageHeader from "../common/PageHeader/PageHeader";
import MoreActionsMenu from "../common/MoreActionsMenu/MoreActionsMenu";
import SearchContainer from "../common/Forms/SearchContainer/SearchContainer";
import { SearchProps } from "../../types";
import { SearchDefaultValue } from "../../store/types";
import {
  EMPTY_GUID,
  SearchFieldsDefaultValue,
} from "../../utility/constants/constants";
import { UserFormContainer } from "../Users/UserFormContainer";
import { useNavigate } from "react-router-dom";

const Organizations: React.FC<OrganizationProps> = (props) => {
  const { data, activateMutation, deleteMutation } = props;
  const {
    state,
    formContext,
    rowEvents,
    actionColumnAnchorEl,
    toggleNewViewHandler,
    toggleEditViewHandler,
    toggleActivateHandler,
    toggleDeactivateHandler,
    handleDeactivate,
    handleActivate,
    onShowSuccess,
    onAddSnackbarClose,
    handleActionClick,
    setActionColumnAnchorEl,
    handleActiveToggleChange,
  } = useOrganization({
    deleteMutation: deleteMutation,
    activateMutation: activateMutation,
  });

  const [addUser, setAddUser] = useState(false);

  const openMenu = Boolean(actionColumnAnchorEl);
  const navigate = useNavigate();

  const columns = [
    {
      dataField: "id",
      text: "ID",
      hidden: true,
    },
    {
      dataField: "name",
      text: "Name",
      sort: true,
    },
    {
      dataField: "description",
      text: "Description",
      sort: true,
    },
    {
      dataField: "websiteUrl",
      text: "Website Url",
      sort: false,
    },
    {
      dataField: "mainPhone",
      text: "Phone",
      sort: false,
    },
    {
      dataField: "isActive",
      text: "Status",
      sort: false,
      formatter: (cell: boolean) => {
        return <Chip label={cell ? "Active" : "Inactive"} />;
      },
    },
    {
      dataField: "action",
      text: "",
      style: { width: "25px" },
      isDummyField: true,
      formatter: () => {
        return (
          <MoreActionButton handleActionClick={(e) => handleActionClick(e)} />
        );
      },
    },
  ];

  const toggleAddUserHandler = () => {
    setAddUser((prevState) => !prevState);
  };

  const searchProps: SearchProps<OrganizationDto> = {
    searchValue: formContext?.searchValue ?? SearchDefaultValue,
    searchFields: { ...SearchFieldsDefaultValue, name: true },
    setSearchValue: formContext?.setSearchValue,
    refetchGrid: formContext?.refetchGrid,
  };

  const handleLink = () => {
    const id = formContext?.data?.id;
    navigateToOrganization(id);
  };

  function navigateToOrganization(id: string | null | undefined) {
    navigate(`/organization/${id}`);
  }

  return (
    <>
      <>
        <PageHeader>Organizations</PageHeader>
        <SearchContainer
          searchProps={searchProps}
          activeToggle={formContext?.activeToggle ?? true}
          handleActiveToggleChange={handleActiveToggleChange}
          toggleNewViewHandler={toggleNewViewHandler}
        />
      </>

      <Drawer show={state.toggleNewForm}>
        {state.toggleNewForm && (
          <OrganizationFormContainer
            onClose={toggleNewViewHandler}
            onShowSuccess={onShowSuccess}
          />
        )}
      </Drawer>

      <Drawer show={state.toggleEditForm}>
        <></>
        {state.toggleEditForm && formContext?.data && (
          <OrganizationFormContainer
            onClose={toggleEditViewHandler}
            onShowSuccess={onShowSuccess}
          />
        )}
      </Drawer>

      <DataGrid
        columns={columns}
        data={data}
        keyField="id"
        rowEvents={rowEvents}
        totalSize={formContext?.paging?.totalSize}
        remote={true}
        page={formContext?.paging?.currentPage}
        pageSize={formContext?.pageSize}
        handlePageChange={async (newPage, pageSize, sortField, sortOrder) => {
          if (formContext?.setCurrentPage && newPage) {
            await formContext?.setCurrentPage(newPage);
          }
          if (formContext?.setCurrentPageSize && pageSize) {
            await formContext?.setCurrentPageSize(pageSize);
          }
          if (formContext?.setSortBy && sortField && sortOrder) {
            await formContext?.setSortBy({
              field: sortField,
              direction: sortOrder === "asc" ? 1 : -1,
            });
          }
          formContext?.refetchGrid();
        }}
      />

      {actionColumnAnchorEl && (
        <MoreActionsMenu
          isOpen={openMenu}
          target={actionColumnAnchorEl}
          toggle={() => {
            setActionColumnAnchorEl(null);
          }}
          onViewClick={() => {
            handleLink();
            setActionColumnAnchorEl(null);
          }}
        />
      )}

      <Drawer show={addUser}>
        <UserFormContainer
          organizationId={formContext?.data?.id ?? EMPTY_GUID}
          onClose={toggleAddUserHandler}
          onShowSuccess={onShowSuccess}
        />
      </Drawer>

      <ConfirmationDialog
        open={state.isDeactivateDialogOpen}
        onClose={toggleDeactivateHandler}
        onConfirm={handleDeactivate}
        contentTitle={`Deactivate ${formContext?.data?.name}`}
        contentText={"Are you sure you want to continue?"}
        confirmText={"Yes, Continue"}
        closeText={"No, Go Back"}
      ></ConfirmationDialog>

      <ConfirmationDialog
        open={state.isActivateDialogOpen}
        onClose={toggleActivateHandler}
        onConfirm={handleActivate}
        contentTitle={`Activate ${formContext?.data?.name}`}
        contentText={"Are you sure you want to continue?"}
        confirmText={"Yes, Continue"}
        closeText={"No, Go Back"}
      />

      {state.showSuccessSnackbar && (
        <Snackbar
          open={state.showSuccessSnackbar}
          message={state.successMessage}
          handleClose={onAddSnackbarClose}
          severity="success"
        />
      )}
    </>
  );
};

export default Organizations;
